// Base de conhecimento das funcionalidades da plataforma Kentro.
// Fonte única consumida pelas páginas /funcionalidades e /funcionalidades/[modulo],
// e reutilizável por buscas internas, IAs e llms.txt.

export type SegmentExample = { segment: string; text: string };
export type FeatureFaq = { q: string; a: string };
export type FeatureSubsection = { title: string; body?: string; items?: string[]; example?: string };

export type Feature = {
  slug: string;
  code: string;
  title: string;
  whatIs: string;
  benefit: string;
  where?: string;
  howToUse?: string[];
  examples?: SegmentExample[];
  subsections?: FeatureSubsection[];
  faqs?: FeatureFaq[];
};

export type FeatureModule = {
  slug: string;
  number: number;
  title: string;
  short: string;
  intro: string;
  where?: string;
  metaTitle: string;
  metaDesc: string;
  features: Feature[];
  moduleFaqs?: FeatureFaq[];
};

export const FEATURE_MODULES: FeatureModule[] = [
  // ================== MÓDULO 1 ==================
  {
    slug: "operacao-de-atendimento",
    number: 1,
    title: "Operação de Atendimento",
    short: "Central de atendimento em tempo real, chat interno e tarefas do agente.",
    metaTitle: "Operação de Atendimento — Funcionalidades Kentro",
    metaDesc:
      "Dashboard em tempo real, central de chat omnichannel, chat interno e tarefas do agente: as ferramentas do dia a dia da operação de atendimento na Kentro.",
    intro:
      "Área de trabalho do dia a dia: onde gestores acompanham a operação em tempo real e onde agentes conversam com clientes, colegas e organizam suas tarefas.",
    features: [
      {
        slug: "dashboard-em-tempo-real",
        code: "1.1",
        title: "Dashboard em Tempo Real",
        whatIs:
          "Monitor ao vivo de toda a operação de atendimento — agentes, filas e chats ativos — com os principais indicadores do dia em uma única tela.",
        benefit:
          "O gestor enxerga gargalos no momento em que acontecem, e não só no relatório do fim do mês. Se uma fila acumula clientes esperando ou um agente está sobrecarregado, dá para agir na hora — redistribuindo conversas, tirando agentes de pausa ou reforçando um canal.",
        where: "Menu principal > Dashboard.",
        howToUse: [
          "Acompanhe quantos chats estão ativos, aguardando e finalizados no dia.",
          "Monitore o estado de cada agente (disponível, em atendimento, em pausa).",
          "Use os filtros por estado e origem para ver só o que interessa (ex.: apenas WhatsApp, apenas aguardando).",
        ],
        examples: [
          { segment: "Clínica", text: "A recepção vê em tempo real quantos pacientes aguardam resposta no WhatsApp e prioriza confirmações de consulta do dia." },
          { segment: "Provedor de internet (ISP)", text: "Durante uma queda de sinal em um bairro, o gestor acompanha o pico de chamados e realoca agentes do comercial para o suporte." },
          { segment: "E-commerce", text: "Na Black Friday, o dono acompanha o volume de dúvidas por canal e identifica que o Instagram está represando conversas." },
        ],
        faqs: [
          { q: "Consigo ver quais agentes estão em pausa agora?", a: "Sim. O Dashboard mostra o estado de cada agente em tempo real, incluindo quem está em pausa e há quanto tempo." },
          { q: "O Dashboard mostra dados de quais canais?", a: "De todos os canais/filas configurados na sua conta — WhatsApp, Instagram, Facebook, WebChat e chat interno — com filtro por origem." },
          { q: "Posso deixar o Dashboard aberto em uma TV na operação?", a: "Sim, é um uso comum: muitas operações exibem o Dashboard em um telão para toda a equipe acompanhar os indicadores do dia." },
        ],
      },
      {
        slug: "atendimentos-central-de-chat",
        code: "1.2",
        title: "Atendimentos (Central de Chat)",
        whatIs:
          "A central de atendimento ao cliente da Kentro — a tela onde os agentes recebem e respondem conversas de todos os canais, organizadas por fila e canal.",
        benefit:
          "Acabou o \"cada canal em uma aba\": WhatsApp, Instagram, Facebook e WebChat chegam em um só lugar, com o histórico completo do cliente ao lado da conversa. O cliente não repete a história, o agente responde mais rápido e nenhuma mensagem fica sem resposta.",
        where: "Menu principal > Atendimentos.",
        howToUse: [
          "Receba conversas distribuídas automaticamente conforme a estratégia da fila.",
          "Consulte a ficha do cliente, o histórico e os cartões de informação durante o atendimento.",
          "Use mensagens pré-definidas, arquivos da galeria e produtos do catálogo para agilizar respostas.",
          "Encerre com motivo de encerramento e, se configurado, dispare pesquisa de satisfação automaticamente.",
        ],
        examples: [
          { segment: "Imobiliária", text: "O corretor atende no WhatsApp um lead vindo de anúncio, consulta no histórico que ele já pediu informações de outro imóvel no mês passado e personaliza a oferta." },
          { segment: "Escola", text: "A secretaria responde pais pelo WhatsApp e leads de matrícula pelo Instagram na mesma tela, sem trocar de sistema." },
          { segment: "Farmácia", text: "O atendente recebe pedidos pelo WhatsApp, envia o catálogo de produtos e fecha a venda na própria conversa." },
        ],
        faqs: [
          { q: "O cliente percebe que estou usando a Kentro?", a: "Não. O cliente conversa normalmente pelo canal dele (WhatsApp, Instagram etc.); a Kentro organiza tudo do lado da empresa." },
          { q: "Vários agentes podem atender pelo mesmo número de WhatsApp?", a: "Sim. Um número, toda a equipe atendendo simultaneamente, com distribuição automática das conversas." },
          { q: "Consigo transferir uma conversa para outro agente ou fila?", a: "Sim, atendimentos podem ser transferidos entre agentes e filas, mantendo o histórico da conversa." },
        ],
      },
      {
        slug: "chat-interno",
        code: "1.3",
        title: "Chat Interno",
        whatIs: "Canal de comunicação entre os agentes e equipes dentro da própria plataforma, com atalhos rápidos.",
        benefit:
          "A equipe resolve dúvidas internas sem sair da tela de atendimento e sem misturar assunto de trabalho no WhatsApp pessoal. Menos troca de aplicativo, mais agilidade na resposta ao cliente.",
        where: "Menu principal > Chat interno. Os grupos são configurados em Configurações > Grupos > Grupos de Chat Interno.",
        howToUse: [
          "Converse individualmente com colegas ou em grupos por equipe (ex.: \"Suporte\", \"Comercial\").",
          "Use os atalhos rápidos para acessar conversas frequentes.",
          "Tire dúvidas com um supervisor enquanto atende um cliente, sem sair da plataforma.",
        ],
        examples: [
          { segment: "Exemplo prático", text: "O agente de vendas recebe uma pergunta técnica no WhatsApp, aciona o time de suporte pelo chat interno, recebe a resposta e responde o cliente na mesma conversa — em segundos e sem transferir o atendimento." },
        ],
        faqs: [
          { q: "O cliente vê as mensagens do chat interno?", a: "Não. O chat interno é 100% privado entre os usuários da plataforma." },
          { q: "Posso criar grupos por setor?", a: "Sim. Em Configurações > Grupos > Grupos de Chat Interno você agrupa usuários por equipe ou assunto." },
        ],
      },
      {
        slug: "tarefas-e-acoes-do-agente",
        code: "1.4",
        title: "Tarefas e Ações do Agente",
        whatIs:
          "Agenda de trabalho do agente dentro da Kentro — ligações, chats agendados, reuniões e ações personalizadas, com filtros por prazo.",
        benefit:
          "O follow-up deixa de depender da memória. Cada \"te ligo amanhã\" vira uma tarefa com prazo, e o agente começa o dia sabendo exatamente quem contatar — aumentando conversão de vendas e qualidade do pós-venda.",
        where: "Menu principal > Tarefas.",
        howToUse: [
          "Crie tarefas de ligação, chat, reunião ou ações personalizadas vinculadas a contatos e oportunidades.",
          "Filtre por prazo (hoje, atrasadas, próximas) para priorizar o dia.",
          "Combine com etiquetas de tarefas para organizar por tipo ou campanha.",
        ],
        examples: [
          { segment: "Concessionária", text: "O vendedor agenda tarefa de ligação 3 dias após o test-drive para fechar a proposta." },
          { segment: "Clínica", text: "A equipe agenda contato de retorno 6 meses após a consulta para reativar pacientes." },
          { segment: "ISP", text: "O comercial agenda follow-up com condomínios que pediram proposta de link dedicado." },
        ],
        faqs: [
          { q: "As tarefas aparecem só para quem criou?", a: "As tarefas são vinculadas ao agente responsável; gestores acompanham as tarefas da equipe conforme suas permissões." },
          { q: "Consigo ver as tarefas atrasadas?", a: "Sim, o filtro por prazo destaca tarefas vencidas, do dia e futuras." },
        ],
      },
    ],
  },

  // ================== MÓDULO 2 ==================
  {
    slug: "crm-e-funis-de-vendas",
    number: 2,
    title: "CRM e Funis de Vendas",
    short: "Funis Kanban integrados ao atendimento, com automações de ganho, perda e atraso.",
    metaTitle: "CRM e Funis de Vendas — Funcionalidades Kentro",
    metaDesc:
      "CRM Kanban da Kentro com funis personalizáveis, integração ao atendimento e automações por estágio, ganho, perda e atraso — a negociação vive junto com a conversa.",
    intro:
      "O CRM da Kentro organiza as oportunidades de venda em funis visuais (Kanban), integrados ao atendimento: a conversa do WhatsApp e a negociação vivem no mesmo lugar.",
    features: [
      {
        slug: "funis-de-vendas-kanban",
        code: "2.1",
        title: "Funis de Vendas (Kanban)",
        whatIs:
          "Quadros visuais no estilo Kanban onde cada oportunidade é um cartão que avança por estágios personalizáveis (ex.: Novo Lead → Em negociação → Proposta enviada → Ganho/Perda). A empresa pode ter vários funis e alternar pelo seletor no topo.",
        benefit:
          "O gestor bate o olho e sabe exatamente quantas negociações existem, em que fase estão e onde travam. Nenhum lead esquecido, previsibilidade de vendas e processo comercial que funciona mesmo quando o vendedor está de férias.",
        where: "Menu principal > CRM (Funis de vendas). Troque de funil pelo seletor no topo.",
        howToUse: [
          "Criar oportunidades manualmente, a partir de um atendimento ou automaticamente via automações e captura de webhook.",
          "Mover entre estágios arrastando o cartão conforme a negociação avança.",
          "Marcar ganho ou perda ao final — alimentando relatórios e disparando automações de ganho/perda.",
          "Excluir ou limpar funil quando precisar reorganizar o processo.",
        ],
        examples: [
          { segment: "Imobiliária", text: "Funil com estágios Visita agendada → Visita realizada → Proposta → Documentação → Fechamento; cada corretor enxerga sua carteira." },
          { segment: "Escola/curso", text: "Funil de matrículas no período de captação: Interessado → Visita à escola → Matrícula em andamento → Matriculado." },
          { segment: "ISP", text: "Funil separado para planos residenciais e outro para empresariais/link dedicado, alternando pelo seletor de funis." },
          { segment: "Clínica", text: "Funil de orçamentos: Orçamento enviado → Em avaliação → Aprovado → Agendado." },
        ],
        faqs: [
          { q: "Posso ter mais de um funil?", a: "Sim. Crie quantos funis precisar (ex.: um por produto, unidade ou equipe) e alterne entre eles pelo seletor no topo." },
          { q: "A oportunidade fica ligada à conversa do WhatsApp?", a: "Sim. O CRM é integrado ao atendimento: da oportunidade você acessa o histórico de conversas do contato e vice-versa." },
          { q: "O que acontece quando marco uma oportunidade como ganha?", a: "Ela é registrada nos relatórios e histórico, e pode disparar uma automação de ganho — mensagem de boas-vindas, geração de documento ou movimentação para funil de pós-venda." },
        ],
      },
      {
        slug: "configuracao-do-funil",
        code: "2.2",
        title: "Configuração do Funil",
        whatIs:
          "Painel de configuração de cada funil, aberto pelo ícone de engrenagem, organizado em três abas: Geral, Permissões e Automações.",
        benefit:
          "Cada funil vira uma máquina com regras próprias — quem pode ver, o que acontece automaticamente em cada etapa e quanto tempo uma negociação pode ficar parada antes de gerar alerta ou ação.",
        where: "CRM > selecione o funil > ícone de engrenagem.",
        subsections: [
          {
            title: "Aba Geral",
            items: [
              "Nome e descrição do funil.",
              "Fila para automações: define por qual canal/fila as automações deste funil enviarão mensagens.",
              "Tempo máximo no funil: prazo limite; ao estourar, aciona a automação de atraso.",
              "Automações de ganho, perda e atraso (ex.: mensagem de agradecimento no ganho, pesquisa de motivo na perda, alerta ao gestor no atraso).",
              "Formulários personalizados vinculados às oportunidades.",
              "Templates de documentos: propostas e contratos gerados a partir dos dados da oportunidade.",
              "Grupos de acesso: perfis que terão acesso ao funil.",
            ],
          },
          {
            title: "Aba Permissões",
            body: "Controle fino de quem acessa o funil — por usuário ou grupo de acesso. Útil para separar equipes: o time B2B não vê o funil de varejo, e vice-versa.",
          },
          {
            title: "Aba Automações",
            body: "Regras automáticas vinculadas ao funil — combinadas com o módulo de Automação com contatos e oportunidades, permitem mover cartões, enviar mensagens e criar tarefas conforme a oportunidade avança.",
          },
        ],
        examples: [
          { segment: "Uso completo", text: "Uma imobiliária configura no funil de lançamentos: tempo máximo de 15 dias (automação de atraso avisa o gestor), automação de ganho que envia o contrato gerado pelo template de documento, e permissões apenas para a equipe do lançamento." },
        ],
        faqs: [
          { q: "O que é a \"fila para automações\" do funil?", a: "É o canal (ex.: o número oficial de WhatsApp da empresa) que as automações do funil usarão para enviar mensagens automáticas aos contatos das oportunidades." },
          { q: "Consigo gerar contratos ou propostas automaticamente?", a: "Sim. Vincule modelos de documentos ao funil e gere documentos preenchidos com os dados da oportunidade." },
          { q: "Como restrinjo um funil só para a equipe comercial?", a: "Na aba Permissões do funil, libere o acesso apenas para os usuários ou grupos desejados." },
        ],
      },
    ],
  },

  // ================== MÓDULO 3 ==================
  {
    slug: "relatorios",
    number: 3,
    title: "Relatórios",
    short: "KPIs de agente, dashboards de CRM e tickets, históricos e relatórios operacionais.",
    metaTitle: "Relatórios e KPIs — Funcionalidades Kentro",
    metaDesc:
      "KPIs por agente, dashboards de CRM e tickets, histórico completo de atendimentos e oportunidades, relatórios de filas, tempos e agenda de contatos — tudo com exportação de dados.",
    intro:
      "Relatórios operacionais e gerenciais com atualização e exportação de dados. É onde a operação vira número: produtividade da equipe, desempenho dos canais, resultado do CRM e qualidade do atendimento.",
    where: "Menu principal > Relatórios.",
    features: [
      {
        slug: "kpis-por-agente",
        code: "3.1",
        title: "KPIs por Agente",
        whatIs:
          "Painel com os indicadores individuais de cada agente — quantidade de atendimentos, médias, mensagens trocadas e TMA (Tempo Médio de Atendimento).",
        benefit:
          "Gestão de equipe baseada em dados, não em impressão. Identifique quem precisa de treinamento, quem merece reconhecimento e como equilibrar a carga entre agentes.",
        examples: [{ segment: "Exemplo", text: "O supervisor compara o TMA da equipe e descobre que um agente resolve em 4 minutos o que outros levam 12 — e transforma o método dele em padrão para o time." }],
      },
      {
        slug: "dashboard-de-crm",
        code: "3.2",
        title: "Dashboard de CRM",
        whatIs: "Indicadores consolidados das oportunidades do CRM: volume por estágio, ganhos, perdas e evolução dos funis.",
        benefit: "Visão executiva do comercial em uma tela — quanto há em negociação, quanto foi convertido e onde o funil vaza.",
        examples: [{ segment: "Exemplo", text: "O dono acompanha semanalmente a taxa de conversão e percebe que a maioria das perdas acontece no estágio \"Proposta enviada\" — sinal para revisar preço ou follow-up." }],
      },
      {
        slug: "dashboard-de-crm-por-usuario",
        code: "3.3",
        title: "Dashboard de CRM por Usuário",
        whatIs: "Os mesmos indicadores do Dashboard de CRM, segmentados por usuário/vendedor.",
        benefit: "Ranking claro de desempenho comercial individual — essencial para metas, comissões e coaching de vendas.",
        examples: [{ segment: "Exemplo", text: "A gerente comercial identifica que um vendedor converte muito bem, mas recebe poucos leads — e rebalanceia a distribuição de oportunidades." }],
      },
      {
        slug: "dashboard-de-tickets",
        code: "3.4",
        title: "Dashboard de Tickets",
        whatIs: "Visão dos tickets por cliente/empresa e acompanhamento de SLA (prazo de atendimento acordado).",
        benefit: "Para operações com suporte estruturado (B2B, ISPs, software), garante que nenhum chamado estoure o prazo e mostra quais clientes mais demandam a equipe.",
        examples: [{ segment: "ISP", text: "Monitora tickets de clientes empresariais com SLA de 4 horas e prioriza os que estão perto de vencer." }],
      },
      {
        slug: "historico-de-atendimentos",
        code: "3.5",
        title: "Histórico de Atendimentos",
        whatIs: "Registro completo de todos os chats realizados, com filtro por número ou protocolo e acesso à ficha completa do cliente.",
        benefit: "Rastreabilidade total. Qualquer discussão sobre \"o que foi combinado\" se resolve em segundos localizando a conversa pelo protocolo.",
        examples: [{ segment: "Exemplo", text: "Um cliente contesta um valor cobrado; a empresa localiza o atendimento pelo protocolo e confirma exatamente o que foi prometido, com data e hora." }],
      },
      {
        slug: "reaberturas-agendadas",
        code: "3.6",
        title: "Reaberturas Agendadas",
        whatIs: "Lista dos atendimentos programados para reabrir automaticamente em uma data futura.",
        benefit: "O \"me chama mês que vem\" acontece de verdade — a conversa volta sozinha para a fila no dia certo, sem depender de lembrete manual.",
        examples: [{ segment: "Exemplo", text: "O cliente diz que só pode decidir após o dia 10; o agente agenda a reabertura e a Kentro devolve o atendimento à fila automaticamente na data." }],
      },
      {
        slug: "historico-de-oportunidades",
        code: "3.7",
        title: "Histórico de Oportunidades",
        whatIs: "Registro de todas as oportunidades do CRM — criadas, ganhas, perdidas e movimentadas.",
        benefit: "Memória completa do comercial para auditoria, análise de safra de leads e revisão de negociações antigas.",
        examples: [{ segment: "Exemplo", text: "No fim do trimestre, o gestor analisa as oportunidades perdidas por motivo e descobre o padrão mais comum de objeção." }],
      },
      {
        slug: "relatorio-de-agentes",
        code: "3.8",
        title: "Relatório de Agentes",
        whatIs: "Desempenho consolidado por agente: chamadas, chats e disponibilidade.",
        benefit: "Visão completa da produtividade individual, combinando volume de trabalho e tempo disponível.",
        examples: [{ segment: "Exemplo", text: "O RH usa o relatório mensal de agentes como insumo objetivo para avaliação de desempenho." }],
      },
      {
        slug: "relatorio-de-filas",
        code: "3.9",
        title: "Relatório de Filas",
        whatIs: "Desempenho por fila/canal: total de atendimentos, abandonos, SLA e tempos médios.",
        benefit: "Mostra a saúde de cada canal. Abandono alto em uma fila significa clientes desistindo antes de serem atendidos — dinheiro saindo pela porta.",
        examples: [{ segment: "Exemplo", text: "A empresa descobre que a fila do Instagram tem tempo de espera 3x maior que a do WhatsApp e realoca um agente para equilibrar." }],
      },
      {
        slug: "relatorio-de-tempos",
        code: "3.10",
        title: "Relatório de Tempos",
        whatIs: "Disponibilidade e pausas por agente — quanto tempo cada um ficou disponível, em atendimento ou em pausa (e por qual motivo).",
        benefit: "Transparência da jornada de trabalho e controle dos limites de pausa definidos pela empresa.",
        examples: [{ segment: "Exemplo", text: "O gestor verifica se as pausas de almoço estão dentro do tempo máximo configurado e identifica excessos recorrentes." }],
      },
      {
        slug: "agenda-de-contatos",
        code: "3.11",
        title: "Agenda de Contatos",
        whatIs: "A base central de contatos da empresa, com importação, exportação e gestão de duplicados.",
        benefit: "Uma base única e limpa — fim das listas espalhadas em planilhas e celulares de vendedores. Alicerce para campanhas de disparo e automações.",
        examples: [{ segment: "Exemplo", text: "A empresa importa a planilha de clientes do sistema antigo, a Kentro aponta os duplicados e a base fica pronta para uma campanha de reativação." }],
      },
    ],
    moduleFaqs: [
      { q: "Consigo exportar os relatórios?", a: "Sim. Os relatórios possuem atualização e exportação de dados para análise externa (ex.: Excel/planilhas)." },
      { q: "O que é TMA?", a: "Tempo Médio de Atendimento — quanto tempo, em média, cada atendimento leva do início ao encerramento. Um dos principais indicadores de eficiência." },
      { q: "O que é SLA?", a: "Service Level Agreement — o prazo-alvo de atendimento definido pela empresa (ex.: responder em até 5 minutos, resolver ticket em até 4 horas). A Kentro acompanha o cumprimento por fila e por ticket." },
      { q: "Consigo criar um relatório do meu jeito?", a: "Sim, através dos Relatórios Personalizados, que permitem montar relatórios sob medida." },
      { q: "Como encontro uma conversa antiga específica?", a: "Em Relatórios > Histórico de atendimentos, filtre pelo número do cliente ou pelo protocolo do atendimento." },
    ],
  },

  // ================== MÓDULO 4 ==================
  {
    slug: "configuracoes",
    number: 4,
    title: "Configurações",
    short: "Canais, usuários, IA, automações, cadastros, etiquetas, grupos e tickets.",
    metaTitle: "Configurações e Administração — Funcionalidades Kentro",
    metaDesc:
      "Área administrativa da Kentro: filas/canais, usuários, pausas, campanhas, pesquisas, assistentes de IA, automações, cadastros, etiquetas, grupos, tickets e monitoramento.",
    intro:
      "Área onde o administrador molda a Kentro à operação da empresa: canais, usuários, automações, IA, cadastros e regras.",
    where: "Menu de configuração (engrenagem).",
    features: [
      {
        slug: "filas-canais",
        code: "4.1",
        title: "Filas / Canais",
        whatIs:
          "Cadastro e configuração dos canais de atendimento — WhatsApp, Facebook, Instagram, WebChat e Chat interno. Cada canal conectado é uma \"fila\" com regras próprias.",
        benefit:
          "É aqui que a empresa profissionaliza seus canais: conecta o WhatsApp oficial, define horários, mensagens automáticas, distribuição de conversas e limite por agente. Regras claras = atendimento consistente, mesmo com equipe grande.",
        where: "Configurações > Filas / Canais > selecione ou crie uma fila.",
        subsections: [
          {
            title: "Aba Conexão",
            body: "Status do canal em tempo real: conectar/desconectar, QR Code (quando aplicável) e verificação de saúde da conexão.",
            example: "Para ativar o WhatsApp, o administrador escaneia o QR Code ou configura a API Oficial e confirma o status \"conectado\". Se as mensagens pararem de chegar, o botão de verificar saúde diagnostica a conexão.",
          },
          {
            title: "Aba Configurações",
            items: [
              "Limite de atendimentos por agente: evita sobrecarga (ex.: máximo de 5 conversas simultâneas).",
              "Motivo de encerramento: exige que o agente classifique o encerramento.",
              "Envio para URA: direciona o cliente para o menu automático antes do agente.",
              "Travar atendimento: impede que outro agente assuma uma conversa em andamento.",
              "Horário de atendimento: vincula a fila a um horário cadastrado; fora dele, dispara mensagem automática.",
              "Fechar por inatividade: encerra conversas sem resposta do cliente após o tempo definido.",
              "Histórico multifila: exibe ao agente o histórico do contato em outras filas.",
              "Estratégia de distribuição: define como as conversas são entregues (distribuição automática/balanceada).",
              "Importar histórico do canal e configurar gatilhos e motivos de encerramento.",
            ],
            example: "Uma clínica define horário de 7h às 19h com mensagem fora do horário, limite de 6 conversas por recepcionista e fechamento por inatividade após 30 minutos — a operação roda sozinha.",
          },
          {
            title: "Aba Mensagens",
            items: [
              "Fora do horário (ex.: \"Nosso atendimento é de 8h às 18h, retornaremos amanhã\").",
              "Fechamento do atendimento.",
              "Inatividade (antes de encerrar por falta de resposta).",
              "Pesquisa de qualidade (convite para avaliar o atendimento).",
              "Agradecimento.",
            ],
            example: "Um e-commerce configura mensagem de fechamento com link do pedido e pesquisa de qualidade automática — coletando NPS sem esforço da equipe.",
          },
          {
            title: "Aba Catálogo e Produtos",
            body: "Grupos de produtos vinculados à fila, permitindo que agentes enviem produtos do catálogo durante o atendimento.",
          },
          {
            title: "Aba API e Webhooks",
            body: "Chave de autenticação (apikey) da fila para integrações externas — conecte a Kentro a ERPs, sites e sistemas próprios.",
            example: "O sistema de gestão do ISP usa a apikey da fila para disparar avisos automáticos de vencimento de boleto pelo WhatsApp.",
          },
          {
            title: "Aba Configurações de IA",
            body: "Parâmetros de IA aplicados à fila — define como os assistentes de IA atuam naquele canal.",
            example: "Na fila comercial, a IA qualifica leads 24/7; na fila de suporte, responde dúvidas frequentes e transfere casos complexos para humanos.",
          },
          {
            title: "Aba Assistência / Suporte Remotos",
            body: "Conteúdo e instruções de assistência com editor de texto — material de apoio vinculado à fila.",
          },
          {
            title: "Aba Elemento Web (canais web)",
            body: "Script incorporável do widget de chat — copie e cole no site da empresa para ativar o chat online integrado à Kentro.",
            example: "A imobiliária cola o script no site; visitantes conversam pelo widget e as mensagens caem na central de atendimentos, distribuídas para os corretores.",
          },
        ],
        faqs: [
          { q: "Como conecto meu WhatsApp na Kentro?", a: "Em Configurações > Filas/Canais, crie ou selecione a fila de WhatsApp e use a aba Conexão — via QR Code ou API Oficial da Meta (recomendada para mais estabilidade, segurança e recursos como templates e campanhas)." },
          { q: "Posso ter mais de um número de WhatsApp?", a: "Sim. Cada número é uma fila independente, com regras, agentes, mensagens e relatórios próprios." },
          { q: "O que acontece se um cliente chamar fora do horário?", a: "Ele recebe automaticamente a mensagem de fora do horário configurada, e o atendimento fica registrado para tratamento no próximo expediente." },
          { q: "Como coloco o chat da Kentro no meu site?", a: "Crie uma fila de WebChat e copie o script da aba Elemento web para o código do seu site." },
          { q: "Como as conversas são divididas entre os agentes?", a: "Pela estratégia de distribuição configurada na fila, respeitando o limite de atendimentos simultâneos de cada agente." },
        ],
      },
      {
        slug: "usuarios",
        code: "4.2",
        title: "Usuários",
        whatIs:
          "Cadastro dos usuários/agentes da plataforma, com controle completo de dados, permissões e parâmetros de atendimento.",
        benefit:
          "Cada pessoa acessa exatamente o que precisa — o vendedor não mexe nas configurações, o supervisor monitora a equipe, o administrador controla tudo. Segurança e organização para crescer a equipe sem perder o controle.",
        where: "Configurações > Usuários.",
        subsections: [
          { title: "Abas do usuário", items: [
            "Geral: dados cadastrais, ramal e grupos de acesso.",
            "Permissões: o que o usuário pode ver e fazer.",
            "Filas e Atendimento: em quais filas atua e seus parâmetros.",
            "Tickets: capacidade e filas de ticket.",
            "Monitoramento: limites, filtros, filas e perfis.",
            "Notificações Push: notificações que recebe.",
            "Variáveis: variáveis personalizadas do usuário (ex.: nome de exibição, assinatura).",
          ] },
        ],
        examples: [{ segment: "Exemplo", text: "Ao contratar um novo atendente, o gestor cria o usuário, vincula às filas de WhatsApp e Instagram do suporte, define limite de conversas simultâneas e aplica o grupo de acesso \"Agente\" — em minutos a pessoa está atendendo, sem acesso a áreas administrativas." }],
        faqs: [
          { q: "Como adiciono um novo atendente?", a: "Em Configurações > Usuários, cadastre os dados na aba Geral, defina as permissões e vincule as filas na aba Filas e Atendimento." },
          { q: "Posso limitar o que cada agente vê?", a: "Sim, pelas permissões individuais e pelos grupos de acesso." },
          { q: "O que são as variáveis do usuário?", a: "Dados personalizados de cada usuário que podem ser usados dinamicamente em mensagens e automações (ex.: inserir o nome do agente na saudação automática)." },
        ],
      },
      {
        slug: "pausas",
        code: "4.3",
        title: "Pausas",
        whatIs:
          "Cadastro dos motivos de pausa dos agentes (ex.: almoço, café, treinamento), com tempo máximo, quantidade de vezes por dia e ação associada.",
        benefit:
          "Pausas organizadas e mensuráveis. A empresa define as regras do jogo e o Relatório de Tempos mostra se estão sendo cumpridas — sem microgerenciamento.",
        where: "Configurações > Pausas.",
        examples: [{ segment: "Exemplo", text: "\"Almoço\" com tempo máximo de 1h, limite 1 vez ao dia; \"Pausa rápida\" com 15 minutos até 2 vezes. O Dashboard mostra quem está em pausa em tempo real." }],
        faqs: [
          { q: "O agente pode estourar o tempo de pausa?", a: "O motivo de pausa possui tempo máximo e ação configurável para controle de excessos, e tudo fica registrado no Relatório de Tempos." },
          { q: "As pausas afetam a distribuição de conversas?", a: "Sim — agente em pausa não recebe novas conversas até voltar ao estado disponível." },
        ],
      },
      {
        slug: "campanhas",
        code: "4.4",
        title: "Campanhas",
        whatIs:
          "Campanhas de disparo em massa por template, com definição de fila, contagem de envios e acompanhamento do estado da campanha.",
        benefit:
          "Marketing ativo direto no canal que o cliente mais lê: o WhatsApp. Promoções, avisos e reativação de base em escala, usando templates aprovados (WA Cloud API) — com a segurança da API Oficial e sem risco de banimento.",
        where: "Configurações > Campanhas.",
        howToUse: [
          "Escolha o template aprovado (Templates WA Cloud API).",
          "Selecione a fila de disparo e o público (contatos da Agenda).",
          "Acompanhe a contagem de envios e o estado da campanha em tempo real.",
        ],
        examples: [
          { segment: "E-commerce", text: "Disparo de oferta relâmpago para a base de clientes com template aprovado." },
          { segment: "Clínica", text: "Lembrete em massa de campanha de vacinação ou check-up anual." },
          { segment: "ISP", text: "Aviso de manutenção programada para os clientes de uma região." },
          { segment: "Escola", text: "Comunicado de rematrícula para todos os responsáveis." },
        ],
        faqs: [
          { q: "Disparo em massa não bloqueia o WhatsApp?", a: "Usando a API Oficial da Meta com templates aprovados, os disparos seguem as regras do WhatsApp — é o caminho seguro e profissional para envio em massa." },
          { q: "Consigo acompanhar quantas mensagens já foram enviadas?", a: "Sim, cada campanha exibe contagem de envios e estado (em andamento, concluída etc.)." },
          { q: "Quem responder à campanha cai onde?", a: "Na fila de atendimento vinculada ao disparo — e a conversa pode seguir com agente humano, URA ou assistente de IA." },
        ],
      },
      {
        slug: "pesquisas",
        code: "4.5",
        title: "Pesquisas",
        whatIs: "Configuração das pesquisas de satisfação aplicadas ao final dos atendimentos.",
        benefit: "Feedback contínuo e automático. A empresa mede a qualidade do atendimento (satisfação/nota) sem esforço manual, e os resultados aparecem nos relatórios.",
        where: "Configurações > Pesquisas.",
        examples: [{ segment: "Exemplo", text: "Ao encerrar cada atendimento, o cliente recebe \"De 1 a 5, como você avalia nosso atendimento?\" — e o gestor acompanha a nota média por agente e por fila." }],
        faqs: [
          { q: "A pesquisa é enviada em todos os atendimentos?", a: "Você configura em quais filas a pesquisa é aplicada, através da mensagem de pesquisa de qualidade da fila." },
          { q: "Onde vejo os resultados?", a: "Nos relatórios da plataforma, junto aos indicadores de qualidade da operação." },
        ],
      },
      {
        slug: "assistentes-ia",
        code: "4.6",
        title: "Assistentes (IA)",
        whatIs:
          "Área de criação e gestão dos Assistentes de IA da Kentro — modelos de inteligência artificial que atendem, qualificam e executam ações. O editor de cada assistente possui as abas: Geral, Conhecimento, Procedimentos, Funções, Conectores, MCP, Avançado e Debug.",
        benefit:
          "Um atendente que nunca dorme. O assistente responde na hora, 24/7, com o conhecimento da sua empresa — reduzindo drasticamente a carga da equipe humana. É a funcionalidade que permite escalar o atendimento sem escalar a folha de pagamento.",
        where: "Configurações > Assistentes (IA).",
        subsections: [
          { title: "Abas do editor do assistente", items: [
            "Geral: identidade e comportamento do assistente (nome, personalidade, instruções).",
            "Conhecimento: base de informações que o assistente usa para responder — documentos, artigos e conteúdo da empresa.",
            "Procedimentos: roteiros e processos que o assistente segue (ex.: passo a passo de agendamento).",
            "Funções: ações que o assistente executa (ex.: consultar dados, registrar informações).",
            "Conectores: integrações com sistemas externos.",
            "MCP: conexão com servidores Model Context Protocol para ampliar capacidades com ferramentas externas.",
            "Avançado: parâmetros técnicos do modelo.",
            "Debug: ambiente de teste para validar o comportamento antes de colocá-lo em produção.",
          ] },
        ],
        howToUse: [
          "Crie o assistente, alimente a aba Conhecimento com informações da empresa.",
          "Defina procedimentos e funções.",
          "Teste na aba Debug e vincule-o a uma fila pela aba Configurações de IA da fila.",
        ],
        examples: [
          { segment: "Clínica", text: "Assistente responde convênios aceitos, preços e horários, e conduz o pré-agendamento de consultas de madrugada." },
          { segment: "ISP", text: "IA faz a triagem de \"sem internet\" (orienta reiniciar o roteador, verifica se há massiva na região) e abre ticket técnico apenas nos casos reais." },
          { segment: "E-commerce", text: "Assistente informa status do pedido e política de troca, liberando a equipe para vendas." },
          { segment: "Imobiliária", text: "IA qualifica o lead (região, faixa de preço, financiamento) antes de passar ao corretor — que recebe a conversa já contextualizada." },
        ],
        faqs: [
          { q: "A IA da Kentro responde qualquer pergunta?", a: "Ela responde com base no Conhecimento que você fornecer e nos procedimentos configurados — por isso responde sobre o seu negócio, do seu jeito, sem inventar informações de fora." },
          { q: "E se a IA não souber responder?", a: "Configure procedimentos para que ela transfira a conversa para um agente humano na fila adequada." },
          { q: "Posso testar antes de ativar para clientes?", a: "Sim, a aba Debug permite conversar com o assistente e ajustar o comportamento antes de vinculá-lo a uma fila." },
          { q: "O que é MCP?", a: "Model Context Protocol — padrão aberto que permite conectar o assistente a ferramentas e sistemas externos, ampliando o que ele consegue consultar e executar." },
        ],
      },
      {
        slug: "automacao",
        code: "4.7",
        title: "Automação",
        whatIs: "Conjunto de ferramentas que faz a Kentro trabalhar sozinha — fluxos, URA, rotinas sobre o CRM, gatilhos e captura de dados externos.",
        benefit: "Cada tarefa repetitiva automatizada é tempo da equipe devolvido para vender e atender melhor. Empresas usam a automação para responder na hora, rotear com precisão e nunca perder um lead por demora.",
        where: "Configurações > Automação.",
        subsections: [
          {
            title: "Fluxo de Automação e URA",
            body: "Construtor visual de fluxos automáticos e menus de atendimento (URA). Monte o caminho que o cliente percorre antes (ou em vez) de falar com um humano: menus de opções, coleta de dados, direcionamento por fila.",
            example: "Clínica: \"Digite 1 para agendar, 2 para resultados, 3 para financeiro\" — cada opção leva à fila certa, com o cliente já identificado. ISP: URA identifica o CPF, verifica massiva na região e já informa a previsão de normalização sem envolver agente.",
          },
          {
            title: "Automação com Contatos e Oportunidades",
            body: "Rotinas automáticas sobre o CRM: mover oportunidades, atualizar contatos, criar tarefas e disparar mensagens conforme regras.",
            example: "Lead sem interação há 7 dias recebe mensagem automática de follow-up e a oportunidade ganha etiqueta \"reaquecimento\".",
          },
          {
            title: "Gatilhos de Atendimento",
            body: "Disparos automáticos por regra durante o atendimento — ações que acontecem quando determinadas condições são atendidas.",
            example: "Atendimento aguardando há mais de 10 minutos notifica o supervisor; encerramento com motivo \"orçamento\" cria oportunidade no funil automaticamente.",
          },
          {
            title: "Gatilhos de Mensagem",
            body: "Disparos automáticos por palavra-chave ou condição na mensagem do cliente.",
            example: "Cliente escreve \"2ª via\" e recebe instantaneamente o boleto/orientação; mensagem contendo \"cancelar\" roteia direto para a fila de retenção com prioridade.",
          },
          {
            title: "Captura de Webhook",
            body: "Recebimento de dados externos via webhook, com chave própria — a porta de entrada de leads e eventos de outros sistemas para dentro da Kentro.",
            example: "O formulário do site ou o lead ad do Facebook envia dados via webhook; a Kentro cria o contato, abre a oportunidade no funil e dispara a primeira mensagem automaticamente — atendimento iniciado em segundos.",
          },
        ],
        faqs: [
          { q: "Preciso saber programar para criar automações?", a: "Não. O construtor de fluxos e URA é visual, e gatilhos são configurados por regras e condições na própria interface." },
          { q: "Qual a diferença entre gatilho de atendimento e gatilho de mensagem?", a: "O gatilho de atendimento reage a condições do atendimento (tempo, status, motivo); o gatilho de mensagem reage ao conteúdo da mensagem do cliente (palavra-chave/condição)." },
          { q: "Consigo integrar o formulário do meu site?", a: "Sim, pela Captura de webhook: seu site envia os dados para a URL/chave da Kentro e o lead entra automaticamente no atendimento e no CRM." },
          { q: "URA funciona no WhatsApp?", a: "Sim — o menu automático funciona nos canais de chat, direcionando o cliente para a fila certa antes do atendimento humano." },
        ],
      },
      {
        slug: "cadastros",
        code: "4.8",
        title: "Cadastros",
        whatIs: "Central de conteúdos e estruturas reutilizáveis da plataforma — tudo que agentes, automações e assistentes de IA usam no dia a dia.",
        benefit: "Uma vez cadastrado, é reaproveitado em toda a operação: mensagens padronizadas, catálogos atualizados, templates aprovados e documentos gerados automaticamente.",
        where: "Configurações > Cadastros.",
        subsections: [
          { title: "Itens disponíveis", items: [
            "Formulários personalizados — coleta de dados estruturados (ex.: qualificação de lead, anamnese simplificada).",
            "Cartões de informação — dados do cliente exibidos ao agente durante o atendimento (ex.: plano contratado, vencimento, status financeiro).",
            "Catálogo de Produtos — produtos que podem ser enviados nas conversas e vinculados às filas.",
            "Mensagens pré-definidas — respostas rápidas reutilizáveis para as dúvidas mais comuns.",
            "Galeria de arquivos — biblioteca central de arquivos (PDFs, imagens, vídeos).",
            "Base de conhecimento — artigos e materiais de apoio para a equipe e para os assistentes de IA.",
            "Novidades — comunicados internos exibidos à equipe.",
            "Templates WA Cloud API — modelos de mensagem do WhatsApp API Oficial usados em campanhas e mensagens ativas.",
            "Modelos HTML — modelos em HTML para conteúdos formatados.",
            "Modelos de documentos — propostas e contratos gerados com dados do sistema.",
            "Horários — cadastro dos horários de atendimento usados pelas filas.",
          ] },
        ],
        faqs: [
          { q: "Qual a diferença entre mensagem pré-definida e template WA Cloud API?", a: "A mensagem pré-definida é uma resposta rápida usada pelo agente dentro de uma conversa em andamento. O template WA Cloud API é um modelo aprovado pela Meta, exigido para mensagens ativas/em massa pela API Oficial do WhatsApp." },
          { q: "Para que serve o cartão de informação?", a: "Para dar contexto instantâneo ao agente: dados relevantes do cliente aparecem junto ao atendimento, sem precisar consultar outro sistema." },
          { q: "Posso gerar contratos automaticamente?", a: "Sim, com os Modelos de documentos, que preenchem automaticamente os dados do contato/oportunidade — inclusive vinculados aos funis do CRM." },
        ],
      },
      {
        slug: "etiquetas",
        code: "4.9",
        title: "Etiquetas",
        whatIs: "Sistema de marcação (tags) para organizar e segmentar registros: etiquetas de Contato, FAQ, Tarefas e CRM; e etiquetas de Chat aplicadas aos atendimentos.",
        benefit: "Organização que vira inteligência: filtre clientes VIP, identifique motivos recorrentes de contato, segmente a base para campanhas e encontre qualquer coisa em segundos.",
        where: "Configurações > Etiquetas.",
        examples: [
          { segment: "Contato \"VIP\"", text: "Prioriza clientes estratégicos e segmenta campanhas exclusivas." },
          { segment: "Chat \"Reclamação\"", text: "Permite ao gestor analisar todos os atendimentos críticos do mês." },
          { segment: "CRM \"Indicação\"", text: "Mostra quanto do funil vem de indicações." },
        ],
        faqs: [
          { q: "Etiqueta de contato e de chat são a mesma coisa?", a: "Não — a etiqueta de contato marca a pessoa/empresa (permanente); a etiqueta de chat marca aquele atendimento específico (contextual)." },
        ],
      },
      {
        slug: "grupos",
        code: "4.10",
        title: "Grupos",
        whatIs: "Estruturas de agrupamento e permissão: Grupos de Chat Interno, Grupos de Acesso (perfis reutilizáveis como \"Agente\", \"Supervisor\", \"Administrador\") e Grupos de visualização de mensagens.",
        benefit: "Governança escalável: em vez de configurar permissões usuário por usuário, defina perfis uma vez e aplique a todos — segurança e padronização conforme a equipe cresce.",
        where: "Configurações > Grupos.",
        examples: [{ segment: "Exemplo", text: "A empresa cria os grupos \"Vendas\" (vê CRM e filas comerciais) e \"Suporte\" (vê tickets e filas de suporte); novos contratados recebem o grupo certo e herdam todas as permissões em um clique." }],
      },
      {
        slug: "ticket",
        code: "4.11",
        title: "Ticket",
        whatIs: "Módulo de tickets da Kentro para suporte estruturado com prazos e fluxo — indicado para operações B2B, técnicas ou com SLA formal.",
        benefit: "Demandas complexas deixam de se perder em conversas: viram tickets com número, responsável, estágio e prazo — e o Dashboard de Tickets cobra o SLA.",
        where: "Configurações > Ticket.",
        subsections: [
          { title: "Configurações disponíveis", items: [
            "Grupos de Serviço — agrupamento dos serviços de ticket.",
            "Classificações — categorias dos tickets.",
            "Motivos — motivos de abertura e encerramento.",
            "Emails — contas de e-mail vinculadas (tickets podem nascer de e-mails).",
            "Estágios — etapas do fluxo (ex.: Aberto → Em análise → Aguardando cliente → Resolvido).",
            "Configurações Gerais — parâmetros gerais do módulo.",
          ] },
        ],
        examples: [
          { segment: "ISP", text: "Chamado técnico vira ticket classificado por tipo (sem conexão, lentidão, mudança de endereço), roteado para a equipe de campo com SLA por plano." },
          { segment: "Software/B2B", text: "Cada solicitação de cliente corporativo vira ticket com estágio visível e histórico completo, alimentando o dashboard por cliente/empresa." },
        ],
        faqs: [
          { q: "Qual a diferença entre atendimento (chat) e ticket?", a: "O chat é a conversa em tempo real; o ticket é a demanda estruturada que pode atravessar dias, com estágios, classificação, responsável e SLA. Um atendimento pode gerar um ticket quando a solução não é imediata." },
          { q: "Tickets funcionam com e-mail?", a: "Sim, contas de e-mail podem ser vinculadas ao módulo para abertura e acompanhamento de tickets." },
        ],
      },
      {
        slug: "monitoramento-de-produtividade",
        code: "4.12",
        title: "Monitoramento de Produtividade",
        whatIs: "Módulo que mede a produtividade digital da equipe: cadastro de Aplicações, Regras de Matching (classificam cada atividade como produtiva/improdutiva) e Perfis de Scoring.",
        benefit: "Visibilidade objetiva de como o tempo de trabalho é usado, com critérios definidos pela própria empresa — gestão por dados, adequada a equipes presenciais e remotas.",
        where: "Configurações > Monitoramento de Produtividade.",
        examples: [{ segment: "Exemplo", text: "A empresa cadastra as aplicações da operação, define que sistemas de trabalho contam como produtivos, e aplica perfis de scoring por função — o comercial tem critérios diferentes do suporte." }],
      },
      {
        slug: "outros-itens-do-menu-de-configuracao",
        code: "4.13",
        title: "Outros Itens do Menu de Configuração",
        whatIs: "Complementos administrativos: Relatórios Personalizados, Log de Auditoria e Configurações Gerais.",
        benefit: "Personalização, rastreabilidade e ajustes finos da conta em um só lugar.",
        subsections: [
          { title: "Itens", items: [
            "Relatórios Personalizados — criação de relatórios sob medida, além dos padrão (ex.: relatório específico com indicadores da diretoria).",
            "Log de Auditoria — registro de todas as ações realizadas na plataforma: quem fez o quê e quando (rastrear alterações de fila, exclusão de contato etc.).",
            "Geral — configurações gerais da conta/plataforma.",
          ] },
        ],
        faqs: [
          { q: "A Kentro registra o que cada usuário faz?", a: "Sim, o Log de Auditoria registra as ações realizadas na plataforma, dando rastreabilidade e segurança à operação." },
        ],
      },
    ],
  },
];

export function findModule(slug: string): FeatureModule | undefined {
  return FEATURE_MODULES.find((m) => m.slug === slug);
}

/** Concatena todos os FAQs de um módulo (por-funcionalidade + FAQs do módulo). */
export function allModuleFaqs(mod: FeatureModule): FeatureFaq[] {
  const list: FeatureFaq[] = [];
  for (const f of mod.features) if (f.faqs) list.push(...f.faqs);
  if (mod.moduleFaqs) list.push(...mod.moduleFaqs);
  return list;
}
